import React, { useState } from "react";
import { Plus, X, Wand2, Loader, Info } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useNavigate } from "react-router-dom";

const MAX = 5;

function emptyJD() {
  return { job_description: "", company_name: "", job_title: "" };
}

export default function BulkTailorPanel({ creditsBalance, isPro, onClose }) {
  const navigate = useNavigate();
  const [jds, setJds] = useState([emptyJD(), emptyJD()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showTips, setShowTips] = useState(false);

  const validCount = jds.filter((j) => (j.job_description || "").trim().length >= 50).length;
  const costPerJob = isPro ? 0 : 5;
  const totalCost = validCount * costPerJob;
  const canSubmit = validCount >= 1 && !submitting && (isPro || creditsBalance >= totalCost);

  const update = (idx, patch) => {
    setJds(jds.map((j, i) => (i === idx ? { ...j, ...patch } : j)));
  };

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        jds: jds.filter((j) => (j.job_description || "").trim().length >= 50),
      };
      const { data } = await api.post("/analyze/bulk", payload);
      navigate(`/bulk/${data.bulk_id}`);
    } catch (e) {
      setSubmitting(false);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="rm-card-static p-6 md:p-8 space-y-5" data-testid="bulk-tailor-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="rm-tag mb-2" style={{ background: "#FFC145", borderColor: "#0F1B2D" }}>
            Bulk mode · up to {MAX} Job Descriptions
          </div>
          <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight">
            Tailor up to 5 resumes <span className="rm-text-orange">in one go.</span>
          </h2>
          <p className="text-sm mt-2" style={{ color: "#475569" }}>
            Paste each Job Description into its own slot. Resmo runs them in parallel and emails you a summary
            when they're all ready (usually 60-120 seconds total). Failed jobs are refunded.
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rm-btn rm-btn-ghost text-xs px-2 py-2"
            data-testid="bulk-close-btn"
            title="Switch back to single mode"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Copy-paste tips */}
      <div>
        <button
          type="button"
          onClick={() => setShowTips((v) => !v)}
          className="rm-btn rm-btn-ghost text-xs px-3 py-1.5"
          data-testid="bulk-tips-toggle"
        >
          <Info size={13} /> {showTips ? "Hide tips" : "Tips for copying job descriptions"}
        </button>
        {showTips && (
          <div
            className="mt-2 p-4 rounded-md text-sm space-y-2"
            style={{ background: "#FEF3C7", border: "2px dashed #F59E0B" }}
            data-testid="bulk-tips-body"
          >
            <div className="font-bold">Best results when you paste:</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>The <strong>role title</strong> at the top</li>
              <li>The <strong>company name</strong> if visible</li>
              <li>The full job description block — responsibilities, requirements, nice-to-haves</li>
            </ul>
            <div className="pt-2">
              <span className="font-bold">Skip:</span> company benefits boilerplate, EEO statements,
              "About Us" marketing fluff.
            </div>
            <div className="pt-2" style={{ color: "#78350F" }}>
              <strong>Pro tip:</strong> On LinkedIn, click "See more" to expand the full Job Description,
              then <kbd className="px-1.5 py-0.5 rounded bg-white border">Cmd/Ctrl + A</kbd> inside
              the description → <kbd className="px-1.5 py-0.5 rounded bg-white border">Cmd/Ctrl + C</kbd>.
              Resmo figures out the title & company automatically.
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="bulk-jd-list">
        {jds.map((jd, i) => {
          const len = (jd.job_description || "").length;
          return (
            <div
              key={i}
              className="p-4 rounded-md space-y-2"
              style={{ border: "3px solid #0F1B2D", background: len >= 50 ? "#ECFDF5" : "white" }}
              data-testid={`bulk-jd-${i}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="rm-label">Job Description #{i + 1}</div>
                {jds.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setJds(jds.filter((_, idx) => idx !== i))}
                    disabled={submitting}
                    className="rm-btn rm-btn-ghost text-xs px-2 py-1"
                    data-testid={`bulk-remove-${i}`}
                    title="Remove this slot"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  className="rm-input"
                  placeholder="Job title (optional)"
                  value={jd.job_title}
                  onChange={(e) => update(i, { job_title: e.target.value })}
                  data-testid={`bulk-job-title-${i}`}
                />
                <input
                  type="text"
                  className="rm-input"
                  placeholder="Company (optional)"
                  value={jd.company_name}
                  onChange={(e) => update(i, { company_name: e.target.value })}
                  data-testid={`bulk-company-${i}`}
                />
              </div>
              <input
                type="url"
                className="rm-input"
                placeholder="Apply URL (optional — enables one-click apply on results page)"
                value={jd.apply_url || ""}
                onChange={(e) => update(i, { apply_url: e.target.value })}
                data-testid={`bulk-apply-url-${i}`}
              />
              <textarea
                className="rm-textarea"
                style={{ minHeight: 140 }}
                placeholder="Paste the full job description here..."
                value={jd.job_description}
                onChange={(e) => update(i, { job_description: e.target.value })}
                data-testid={`bulk-jd-textarea-${i}`}
              />
              <div className="font-mono-rm text-xs flex items-center justify-between">
                <span style={{ color: len >= 50 ? "#10B981" : "#475569" }}>
                  {len} chars {len >= 50 ? "✓" : "· min 50"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setJds([...jds, emptyJD()])}
        disabled={jds.length >= MAX || submitting}
        className="rm-btn rm-btn-ghost text-xs"
        data-testid="bulk-add-jd-btn"
      >
        <Plus size={14} /> Add another Job Description ({jds.length}/{MAX})
      </button>

      {error && (
        <div
          className="p-3 rounded-md font-mono-rm text-sm"
          style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
          data-testid="bulk-error"
        >
          {error}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap pt-3" style={{ borderTop: "2px dashed #0F1B2D" }}>
        <div className="font-mono-rm text-sm" data-testid="bulk-cost-summary">
          {isPro ? (
            <span style={{ color: "#10B981" }}>
              <strong>Pro plan</strong> · {validCount} {validCount === 1 ? "Job Description" : "Job Descriptions"} · no credits charged
            </span>
          ) : (
            <span>
              <strong>{validCount}</strong> {validCount === 1 ? "Job Description" : "Job Descriptions"} ×{" "}
              <strong>{costPerJob}</strong> credits ={" "}
              <strong style={{ color: validCount && creditsBalance < totalCost ? "#DC2626" : "#0F1B2D" }}>
                {totalCost} credits
              </strong>
              <span style={{ color: "#475569" }}> · balance {creditsBalance}</span>
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="rm-btn rm-btn-primary"
          data-testid="bulk-submit-btn"
        >
          {submitting ? <Loader size={16} className="animate-spin" /> : <Wand2 size={16} />}
          {submitting
            ? "Sending to Resmo..."
            : `Tailor all (${isPro ? "Pro" : totalCost + " credits"})`}
        </button>
      </div>
    </div>
  );
}
